import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { appColors, BackrsIcon, Header } from '@backrs_ui/common-ui';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/App';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { SelectMenu } from '@backrs_ui/grow-ui/src/components/SelectMenu/SelectMenu';
import { sessionNames } from '@backrs_ui/grow-ui/src/components/SolutionSession/SessionStrings';
import { SolutionSessionContent } from '@backrs_ui/grow-ui/src/components/SolutionSession/SolutionSessionContent';
import { axiosInstance as axios } from '@backrs_ui/grow-ui/src/core/axios';
import { OpenAIModel, OPENAI_MODELS, MainResponseData } from '@backrs_ui/grow-ui/src/models/models';
import {
  resetMainResponse,
  setBoxData,
  setIsLoadingResources,
} from '@backrs_ui/grow-ui/src/redux/boxData/boxData';
import { setOpenAIModel } from '@backrs_ui/grow-ui/src/redux/coachLubav/coachLubav';
import { initiateSession } from '@backrs_ui/grow-ui/src/redux/coachLubav/coachLubav.thunk';
import { useAppDispatch, useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import {
  getSessionState,
  createSessionAction,
  typeGuardForSolutionSessionId,
} from '@backrs_ui/grow-ui/src/redux/lubavSessionState/lubavSessionState.utils';
import { selectUserId, selectDoesUserHaveRole } from '@backrs_ui/grow-ui/src/redux/user/user';
import { setLubavSession } from '@backrs_ui/grow-ui/src/utils/setLubavSession';

import { Box, Typography, IconButton, Tooltip, SxProps, Theme } from '@mui/material';

const { header } = appStrings.modules;

export function SolutionSession() {
  const dispatch = useAppDispatch();
  const location = useLocation();

  const activeSession = setLubavSession(location.pathname, dispatch);

  const userId = useAppSelector(selectUserId);
  const betaMode = useAppSelector(selectDoesUserHaveRole('Lubav Beta'));

  const { chatMode, customPrompt } = useAppSelector(
    (state) => getSessionState(state, activeSession).solutionSession,
  );

  const { sessionId } = useAppSelector((state) => getSessionState(state, activeSession).coachLubav);

  const { openAIModel: LLM } = useAppSelector(
    (state) => getSessionState(state, activeSession).coachLubav,
  );
  const { boxData } = useAppSelector((state) => getSessionState(state, activeSession).boxData);

  useEffect(() => {
    if (!sessionId && userId) {
      dispatch(
        initiateSession({
          axios,
          userId,
          path: APP_ROUTE.SOLUTION_SESSION,
          solutionSessionName: activeSession,
          activeSession,
        }),
      );
    }
  }, [dispatch, sessionId, userId, activeSession]);

  if (!activeSession || !typeGuardForSolutionSessionId(activeSession)) {
    return null;
  }

  const handleUpdatingOpenAIModel = (updatedOpenAIModel: string) => {
    dispatch(createSessionAction(setOpenAIModel, activeSession)(updatedOpenAIModel as OpenAIModel));
  };

  const handleSetBoxData = (mainResponse: MainResponseData) => {
    const updatedBoxData = { ...boxData, mainResponse };

    dispatch(createSessionAction(setBoxData, activeSession)(updatedBoxData));
  };

  async function updateResponse() {
    if (!activeSession) return;

    dispatch(createSessionAction(setIsLoadingResources, activeSession)(true));
    dispatch(createSessionAction(resetMainResponse, activeSession)(undefined));

    try {
      const mainResponse = await axios.post('/api/solution-session/get-response', {
        session_id: sessionId,
        user_id: `${userId}`,
        llm: LLM,
        solution_session: activeSession,
        no_resources: false,
        custom_prompt: customPrompt !== '' ? customPrompt : null,
      });

      dispatch(createSessionAction(setIsLoadingResources, activeSession)(false));
      handleSetBoxData(mainResponse.data.output);
    } catch (error) {
      dispatch(createSessionAction(setIsLoadingResources, activeSession)(false));

      console.error('Error fetching response:', error);
    }
  }

  const middleSection = (
    <Box sx={middleSectionStyle}>
      {betaMode && (
        <Box>
          <SelectMenu
            value={LLM}
            onChange={handleUpdatingOpenAIModel}
            title={header.modelTitle}
            options={Object.values(OPENAI_MODELS).map((value) => ({
              value,
              label: header.modelMap[value],
            }))}
          />
        </Box>
      )}
    </Box>
  );

  const rightSection = activeSession ? (
    <Box sx={rightSectionStyle}>
      <Typography sx={sessionNameStyle}>{sessionNames[activeSession]}</Typography>
      <Tooltip title="Refresh">
        <IconButton
          onClick={() => updateResponse()}
          sx={getRefreshButtonStyle(!chatMode)}
          disabled={!chatMode}
        >
          <BackrsIcon name="IconRefresh" alt="refresh_resources" />
        </IconButton>
      </Tooltip>
    </Box>
  ) : null;

  return (
    <Box sx={containerStyle}>
      <Header
        title={header.solutionSession}
        middleSection={middleSection}
        rightSection={rightSection}
      />
      <SolutionSessionContent />
    </Box>
  );
}

const containerStyle: SxProps<Theme> = {
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
};

const rightSectionStyle: SxProps<Theme> = {
  display: 'flex',
  flexGrow: 1,
  justifyContent: 'space-between',
};

const sessionNameStyle: SxProps<Theme> = {
  fontWeight: 'bold',
  color: appColors.color.blue1,
  whiteSpace: 'nowrap',
  fontSize: '24px',
};

const middleSectionStyle: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  '@media (max-width: 700px)': {
    flexDirection: 'column',
  },
};

const getRefreshButtonStyle = (disabled: boolean): SxProps<Theme> => ({
  width: '40px',
  height: '40px',
  opacity: disabled ? 0.5 : 1,
  pointerEvents: disabled ? 'none' : 'auto',
});
